import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface User {
  id: number
  type: 'parent' | 'admin'
  display_name: string
  line_user_id?: string
  username?: string
  role?: string
  permissions?: string[]
}

interface AuthState {
  user: User | null
  token: string | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (user: User, token: string) => void
  logout: () => void
  setLoading: (loading: boolean) => void
}

// Helper function to validate JWT format
const isValidJWT = (token: string): boolean => {
  const parts = token.split('.')
  return parts.length === 3 && parts.every(part => part.length > 0)
}

// App version to force logout on version changes
const APP_VERSION = '2.0.0' // Updated for LINE OAuth implementation
const VERSION_KEY = 'krudream-app-version'

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      isAuthenticated: false,
      
      login: (user: User, token: string) => {
        set({
          user,
          token,
          isAuthenticated: true,
          isLoading: false
        })
      },
      
      logout: () => {
        // Clear localStorage to ensure no malformed tokens persist
        localStorage.removeItem('auth-storage')
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false
        })
      },
      
      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated
      }),
      onRehydrateStorage: () => (state) => {
        console.log('[Auth Debug] Rehydrating auth store')
        
        // Check app version and clear on upgrade - but be more lenient in development
        const currentVersion = localStorage.getItem(VERSION_KEY)
        const shouldClearForVersion = currentVersion !== APP_VERSION && import.meta.env.PROD
        
        if (shouldClearForVersion) {
          console.log('[Auth Debug] App version updated in production, clearing auth state for re-authentication')
          localStorage.setItem(VERSION_KEY, APP_VERSION)
          localStorage.removeItem('auth-storage')
          if (state) {
            state.user = null
            state.token = null
            state.isAuthenticated = false
          }
          return
        } else if (currentVersion !== APP_VERSION) {
          console.log('[Auth Debug] App version updated in development, but keeping auth state')
          localStorage.setItem(VERSION_KEY, APP_VERSION)
        }
        
        // Validate token on rehydration - but be more lenient
        if (state?.token) {
          console.log('[Auth Debug] Validating token during rehydration')
          if (!isValidJWT(state.token)) {
            console.warn('[Auth Debug] Invalid JWT token found in storage during rehydration')
            // In development, don't clear immediately - let ProtectedRoute handle it
            if (import.meta.env.PROD) {
              state.user = null
              state.token = null
              state.isAuthenticated = false
            }
          } else {
            console.log('[Auth Debug] Token rehydration successful')
          }
        }
      }
    }
  )
)